import React from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FlaggedPage } from './components/FlaggedPage/FlaggedPage';
import { isDevToolsEnabled } from './config/featureFlags';

const OrganizationShellV2 = React.lazy(() => import('./layouts/OrganizationShellV2/OrganizationShellV2'));
const OrganizationPageV2 = React.lazy(() => import('./pages/Organization/OrganizationPageV2'));

const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const VerticalList = React.lazy(() => import('./pages/Vertical/VerticalList'));
const VerticalDetail = React.lazy(() => import('./pages/Vertical/VerticalDetail'));
const VerticalAdd = React.lazy(() => import('./pages/Vertical/VerticalAdd'));
const VerticalEdit = React.lazy(() => import('./pages/Vertical/VerticalEdit'));
const StructureList = React.lazy(() => import('./pages/Structure/StructureList'));
const StructureDetail = React.lazy(() => import('./pages/Structure/StructureDetail'));
const StructureAdd = React.lazy(() => import('./pages/Structure/StructureAdd'));
const StructureEdit = React.lazy(() => import('./pages/Structure/StructureEdit'));
const TribeList = React.lazy(() => import('./pages/Tribe/TribeList'));
const TribeDetail = React.lazy(() => import('./pages/Tribe/TribeDetail'));
const TribeAdd = React.lazy(() => import('./pages/Tribe/TribeAdd'));
const TribeEdit = React.lazy(() => import('./pages/Tribe/TribeEdit'));
const ProductList = React.lazy(() => import('./pages/Product/ProductList'));
const ProductDetail = React.lazy(() => import('./pages/Product/ProductDetail'));
const ProductAdd = React.lazy(() => import('./pages/Product/ProductAdd'));
const ProductEdit = React.lazy(() => import('./pages/Product/ProductEdit'));
const SquadList = React.lazy(() => import('./pages/Squad/SquadList'));
const SquadAdd = React.lazy(() => import('./pages/Squad/SquadAdd'));
const SquadDetail = React.lazy(() => import('./pages/Squad/SquadDetail'));
const SquadEdit = React.lazy(() => import('./pages/Squad/SquadEdit'));
const SquadGuardian = React.lazy(() => import('./pages/Squad/SquadGuardian'));
const PersonList = React.lazy(() => import('./pages/Person/PersonList'));
const PersonListV2 = React.lazy(() => import('./pages/PersonV2/PersonListV2'));
const PersonAdd = React.lazy(() => import('./pages/Person/PersonAdd'));
const PersonDetail = React.lazy(() => import('./pages/Person/PersonDetail'));
const PersonEdit = React.lazy(() => import('./pages/Person/PersonEdit'));
const PersonReactivate = React.lazy(() => import('./pages/Person/PersonReactivate'));
const PersonEditAllocation = React.lazy(() => import('./pages/Person/PersonEditAllocation'));
const PersonHistory = React.lazy(() => import('./pages/Person/PersonHistory'));
const PositionList = React.lazy(() => import('./pages/Position/PositionList'));
const PositionOverview = React.lazy(() => import('./pages/Position/PositionOverview'));
const PositionAdd = React.lazy(() => import('./pages/Position/PositionAdd'));
const PositionEdit = React.lazy(() => import('./pages/Position/PositionEdit'));
const CapacityList = React.lazy(() => import('./pages/Capacity/CapacityList'));
const PlanningAllocation = React.lazy(() => import('./pages/Capacity/PlanningAllocation'));
const SchedulerList = React.lazy(() => import('./pages/Scheduler/SchedulerList'));
const SchedulerHistory = React.lazy(() => import('./pages/Scheduler/SchedulerHistory'));
const SchedulerAdd = React.lazy(() => import('./pages/Scheduler/SchedulerAdd'));
const PermissionManagement = React.lazy(() => import('./pages/permissions/PermissionManagement'));
const RoleAdd = React.lazy(() => import('./pages/permissions/RoleAdd'));
const AdminPanel = React.lazy(() => import('./pages/Admin/AdminPanel'));
const RoleManagementAdd = React.lazy(() => import('./pages/RoleManagement/RoleManagementAdd'));
const RoleManagementEdit = React.lazy(() => import('./pages/RoleManagement/RoleManagementEdit'));
const RoleGroupAdd = React.lazy(() => import('./pages/RoleManagement/RoleGroupAdd'));
const RoleGroupEdit = React.lazy(() => import('./pages/RoleManagement/RoleGroupEdit'));
const CompanyManagementAdd = React.lazy(() => import('./pages/CompanyManagement/CompanyManagementAdd'));
const CompanyManagementDetail = React.lazy(() => import('./pages/CompanyManagement/CompanyManagementDetail'));
const CompanyManagementEdit = React.lazy(() => import('./pages/CompanyManagement/CompanyManagementEdit'));
const FundedByAdd = React.lazy(() => import('./pages/FundedByManagement/FundedByAdd'));
const FundedByEdit = React.lazy(() => import('./pages/FundedByManagement/FundedByEdit'));
const LocationManagementAdd = React.lazy(() => import('./pages/LocationManagement/LocationManagementAdd'));
const LocationManagementEdit = React.lazy(() => import('./pages/LocationManagement/LocationManagementEdit'));
const Forbidden = React.lazy(() => import('./pages/Forbidden'));
const SquadValidationAuditPage = React.lazy(() => import('./pages/ValidationCycleManagement/SquadValidationAuditPage'));

export const routes: RouteObject[] = [
  {
    // Pathless layout route: OrganizationShellV2 is transparent in old-alloc mode
    // (renders <Outlet /> only) and provides the full revamp shell when useRevamp=true.
    // LayoutSelector in App.tsx skips MainLayout entirely when useRevamp=true so that
    // only one shell wraps the page content regardless of which route is active.
    element: <OrganizationShellV2 />,
    children: [
  { path: '/', element: <ProtectedRoute requirePage="dashboard"><Dashboard /></ProtectedRoute> },
  { path: '/vertical', element: <ProtectedRoute requirePage="vertical"><VerticalList /></ProtectedRoute> },
  { path: '/vertical/add', element: <ProtectedRoute requirePage="vertical"><VerticalAdd /></ProtectedRoute> },
  { path: '/vertical/:id', element: <ProtectedRoute requirePage="vertical"><VerticalDetail /></ProtectedRoute> },
  { path: '/vertical/:id/edit', element: <ProtectedRoute requirePage="vertical"><VerticalEdit /></ProtectedRoute> },
  { path: '/tower', element: <ProtectedRoute requirePage="structure"><StructureList /></ProtectedRoute> },
  { path: '/tower/add', element: <ProtectedRoute requirePage="structure"><StructureAdd /></ProtectedRoute> },
  { path: '/tower/:id', element: <ProtectedRoute requirePage="structure"><StructureDetail /></ProtectedRoute> },
  { path: '/tower/:id/edit', element: <ProtectedRoute requirePage="structure"><StructureEdit /></ProtectedRoute> },
  { path: '/tribe', element: <ProtectedRoute requirePage="valueStream"><TribeList /></ProtectedRoute> },
  { path: '/tribe/add', element: <ProtectedRoute requirePage="valueStream"><TribeAdd /></ProtectedRoute> },
  { path: '/tribe/:id', element: <ProtectedRoute requirePage="valueStream"><TribeDetail /></ProtectedRoute> },
  { path: '/tribe/:id/edit', element: <ProtectedRoute requirePage="valueStream"><TribeEdit /></ProtectedRoute> },
  { path: '/product', element: <ProtectedRoute requirePage="product"><ProductList /></ProtectedRoute> },
  { path: '/product/add', element: <ProtectedRoute requirePage="product"><ProductAdd /></ProtectedRoute> },
  { path: '/product/:id', element: <ProtectedRoute requirePage="product"><ProductDetail /></ProtectedRoute> },
  { path: '/product/:id/edit', element: <ProtectedRoute requirePage="product"><ProductEdit /></ProtectedRoute> },
  { path: '/squad', element: <ProtectedRoute requirePage="squad"><SquadList /></ProtectedRoute> },
  { path: '/squad/add', element: <ProtectedRoute requirePage="squad"><SquadAdd /></ProtectedRoute> },
  { path: '/squad/:id', element: <ProtectedRoute requirePage="squad"><SquadDetail /></ProtectedRoute> },
  { path: '/squad/:id/edit', element: <ProtectedRoute requirePage="squad"><SquadEdit /></ProtectedRoute> },
  { path: '/squad/:id/guardian', element: <ProtectedRoute requirePage="squad"><SquadGuardian /></ProtectedRoute> },
  {
    path: '/person',
    element: (
      <ProtectedRoute requirePage="people">
        <FlaggedPage flag="useRevamp" V1={PersonList} V2={PersonListV2} />
      </ProtectedRoute>
    ),
  },
  ...(isDevToolsEnabled
    ? [{ path: '/person-v2', element: <ProtectedRoute requirePage="people"><PersonListV2 /></ProtectedRoute> }]
    : []),
  { path: '/person/add', element: <ProtectedRoute requirePage="people"><PersonAdd /></ProtectedRoute> },
  { path: '/person/:id', element: <ProtectedRoute requirePage="people"><PersonDetail /></ProtectedRoute> },
  { path: '/person/:id/edit', element: <ProtectedRoute requirePage="people"><PersonEdit /></ProtectedRoute> },
  { path: '/person/:id/reactivate', element: <ProtectedRoute requirePage="people"><PersonReactivate /></ProtectedRoute> },
  { path: '/person/:id/editAllocation', element: <ProtectedRoute requirePage="people"><PersonEditAllocation /></ProtectedRoute> },
  { path: '/person/:id/history', element: <ProtectedRoute requirePage="people"><PersonHistory /></ProtectedRoute> },
  { path: '/position', element: <ProtectedRoute requirePage="position"><PositionList /></ProtectedRoute> },
  { path: '/position/overview', element: <ProtectedRoute requirePage="position"><PositionOverview /></ProtectedRoute> },
  { path: '/position/add', element: <ProtectedRoute requirePage="position"><PositionAdd /></ProtectedRoute> },
  { path: '/position/:id/edit', element: <ProtectedRoute requirePage="position"><PositionEdit /></ProtectedRoute> },
  { path: '/capacity', element: <ProtectedRoute requirePage="capacity"><CapacityList /></ProtectedRoute> },
  { path: '/capacity/tower/:id/planning', element: <ProtectedRoute requirePage="capacity"><PlanningAllocation /></ProtectedRoute> },
  { path: '/scheduler', element: <ProtectedRoute requirePage="scheduler"><SchedulerList /></ProtectedRoute> },
  { path: '/scheduler/history', element: <ProtectedRoute requirePage="scheduler"><SchedulerHistory /></ProtectedRoute> },
  { path: '/scheduler/add', element: <ProtectedRoute requirePage="scheduler"><SchedulerAdd /></ProtectedRoute> },
  { path: '/permissions', element: <ProtectedRoute requirePage="permissions"><PermissionManagement /></ProtectedRoute> },
  { path: '/permissions/add', element: <ProtectedRoute requirePage="permissions"><RoleAdd /></ProtectedRoute> },
  { path: '/admin', element: <ProtectedRoute requirePage="admin"><AdminPanel /></ProtectedRoute> },
  {
    path: '/admin/role-management',
    element: (
      <ProtectedRoute requirePage="admin">
        <Navigate to="/admin" replace state={{ tab: 0 }} />
      </ProtectedRoute>
    ),
  },
  { path: '/admin/role-management/role/add', element: <ProtectedRoute requirePage="admin"><RoleManagementAdd /></ProtectedRoute> },
  { path: '/admin/role-management/group/add', element: <ProtectedRoute requirePage="admin"><RoleGroupAdd /></ProtectedRoute> },
  { path: '/admin/role-management/role/:id/edit', element: <ProtectedRoute requirePage="admin"><RoleManagementEdit /></ProtectedRoute> },
  { path: '/admin/role-management/group/:id/edit', element: <ProtectedRoute requirePage="admin"><RoleGroupEdit /></ProtectedRoute> },
  {
    path: '/admin/company-management',
    element: (
      <ProtectedRoute requirePage="admin">
        <Navigate to="/admin" replace state={{ tab: 1 }} />
      </ProtectedRoute>
    ),
  },
  { path: '/admin/company-management/add', element: <ProtectedRoute requirePage="admin"><CompanyManagementAdd /></ProtectedRoute> },
  { path: '/admin/company-management/:id', element: <ProtectedRoute requirePage="admin"><CompanyManagementDetail /></ProtectedRoute> },
  { path: '/admin/company-management/:id/edit', element: <ProtectedRoute requirePage="admin"><CompanyManagementEdit /></ProtectedRoute> },
  {
    path: '/admin/funded-by-management',
    element: (
      <ProtectedRoute requirePage="admin">
        <Navigate to="/admin" replace state={{ tab: 2 }} />
      </ProtectedRoute>
    ),
  },
  { path: '/admin/funded-by-management/add', element: <ProtectedRoute requirePage="admin"><FundedByAdd /></ProtectedRoute> },
  { path: '/admin/funded-by-management/:id', element: <ProtectedRoute requirePage="admin"><FundedByEdit /></ProtectedRoute> },
  {
    path: '/admin/location-management',
    element: (
      <ProtectedRoute requirePage="admin">
        <Navigate to="/admin" replace state={{ tab: 3 }} />
      </ProtectedRoute>
    ),
  },
  { path: '/admin/location-management/add', element: <ProtectedRoute requirePage="admin"><LocationManagementAdd /></ProtectedRoute> },
  { path: '/admin/location-management/:id/edit', element: <ProtectedRoute requirePage="admin"><LocationManagementEdit /></ProtectedRoute> },
  { path: '/validation-cycles/audit', element: <ProtectedRoute requirePage="admin"><SquadValidationAuditPage /></ProtectedRoute> },
  { path: '/forbidden', element: <Forbidden /> },
  // TODO(revamp): /organization entry point for OrganizationShellV2 (US #4260814).
  // routes.tsx keeps its original name — shared infrastructure, not subject to the V2 suffix rule.
  // OrganizationShellV2 is now the root layout route above; /organization renders its page directly.
  { path: '/organization', element: <ProtectedRoute requirePage="organization"><OrganizationPageV2 /></ProtectedRoute> },
    ],
  },
];
